
import { map, apply } from 'ramda';
import path from 'path';
import fs from 'fs';
import {
  fromXml,
  name as modelName,
  getPropertyValue,
  type as modelType,
} from '@bb-cli/portal-model';
import { log, error } from '@bb-cli/base';
import findModelXmls from './find-models';
import findFiles from './find-files';
import mergeItems from './merge-items';
import { fileExists } from './utils';

const IGNORE_FROM_BUILD = [
  '**/node_modules',
  '**/bower_components',
  '**/dist',
  '**/target',
];

const stripModelXmls = map(path.dirname);

const getAssets = (sourceDir) => {
  const assetsFolderDir = path.join(sourceDir, 'assets');
  return fs.existsSync(assetsFolderDir) ? fs.readdirSync(assetsFolderDir) : [];
};

/**
 * Loads item model.xml and return CXP Item
 *
 * @memberof module:@bb-cli/find-and-build
 * @return {CXPItem} CXP Item object
 * @example
 *
 * ``` javascript
 * import { loadItem } from '@bb-cli/find-and-build';
 * const item = loadItem('/path/to/item/srcDir');
 * console.log(`${item.name} is at version ${item.version}`);
 * ```
 */
const loadItem = (sourceDir) => {
  const resolvedSourceDir = path.resolve(sourceDir);
  const modelXMLPath = path.join(resolvedSourceDir, 'model.xml');
  const modelXml = fs.readFileSync(modelXMLPath).toString();
  let model = undefined;
  try {
    log.verbose(`Trying to parse item model:  ${modelXml}`);
    model = fromXml(modelXml);
  } catch (e) {
    log.error(`Error while parsing ${modelXMLPath}`, e);
  }
  return {
    name: modelName(model),
    version: getPropertyValue('version', model),
    srcDir: resolvedSourceDir,
    type: modelType(model),
    main: getPropertyValue('path', model),
    assets: getAssets(resolvedSourceDir),
  };
};

const loadItems = map(loadItem);

const findItemsInDir = (inDir, excludes) =>
  findModelXmls(path.resolve(inDir), excludes)
    .then(stripModelXmls)
    .then(loadItems);

/**
 * Find CXP items.
 *
 * @memberof module:@bb-cli/find-and-build
 * @param {Array.<string>} inDirs Paths to looks in
 * @param {Array.<string>} exclude Array of paths to exclude from packaging
 * @return {Promise.<CXPItem[]>} Array of items found
 * @example
 *
 * ``` javascript
 * import { findItems } from '@bb-cli/find-and-build';
 * findItems(['.'], ['target'])
 *   .then(items => console.log(`${items.length} items`));
 * ```
 */
const findItems = (inDirs, excludes = []) =>
  Promise.all(inDirs.map(d => findItemsInDir(d, excludes)))
    .then(apply(mergeItems));

/**
 * Find CXP items for building. Thisis the same as findItems except it automatically excludes
 * from the search the following dirs:
 *
 * ```
 * node_modules
 * bower_components
 * dist
 * target
 * ```
 *
 * @memberof module:@bb-cli/find-and-build
 * @param {Array.<string>} inDirs Paths to looks in
 * @param {Array.<string>} exclude Array of paths to exclude from packaging
 * @return {Promise.<CXPItem[]>} Array of items found
 * @example
 *
 * ``` javascript
 * import { findItems } from '@bb-cli/find-and-build';
 * findItems(['.'], ['target'])
 *   .then(items => console.log(`${items.length} items`));
 * ```
 */
const findItemsForBuild = (inDirs, excludes = []) => {
  inDirs.forEach(d => {
    if (!fileExists(d)) {
      throw error({
        type: 'E_NOTEXISTS',
        message: `Dir ${d} not found for build`,
        code: 100,
      });
    }
  });

  return findItems(inDirs, IGNORE_FROM_BUILD.concat(excludes));
};

/**
 * @inner
 * @return {string[]} The files for this item
 */
const itemFiles = (item) => {
  const dir = item.distDir || item.srcDir;
  if (!dir) {
    throw error({
      type: 'E_INVALID_CXP_ITEM',
      message: `Item ${item.name} is misssing srcDir`,
    });
  }

  if (!fileExists(dir)) {
    throw error({
      type: 'E_NOTEXISTS',
      message: `Dir ${dir} not found for item`,
    });
  }

  return findFiles(dir);
};

/**
 * Find CXP items.
 *
 * @memberof module:@bb-cli/find-and-build
 * @param {CXPItem[]} items CXP items to load files for
 * @return {Promise.<CXPItem[]>} Array of items with files property set
 * @example
 *
 * ``` javascript
 * import { findItems, findItemFiles } from '@bb-cli/find-and-build';
 * findItems(['.'], ['target'])
 *   .then(findItemFiles)
 *   .then(items => console.log(`${items[0].files} items`));
 * ```
 */
const findItemFiles = (items) => Promise.all(items.map(
  item => itemFiles(item)
    .then(files => Object.assign({}, item, { files }))
));

export default findItems;
export {
  findItemsForBuild,
  loadItem,
  findItemFiles,
};
